import { For, type JSX } from "solid-js";
import type { AuthUser } from "~/features/auth/auth";
import type { TeacherClass } from "~/features/classes/classes";
import { StreetIcon } from "~/ui/Icon";

export type TeacherView = "today" | "students" | "voting" | "goals" | "progress" | "mood" | "feedback" | "report" | "settings" | "help" | "classes" | "school";

const navigation: Array<{ view: TeacherView; icon: string; label: string }> = [
  { view: "today", icon: "home", label: "Zuhause" },
  { view: "students", icon: "people", label: "Schüler" },
  { view: "voting", icon: "clipboard-check", label: "Abstimmung" },
  { view: "goals", icon: "target", label: "Ziele" },
  { view: "progress", icon: "calendar", label: "Wochenverlauf" },
  { view: "mood", icon: "trend", label: "Stimmung" },
  { view: "feedback", icon: "speech-heart", label: "Feedback" },
  { view: "report", icon: "briefcase", label: "Bericht" },
  { view: "settings", icon: "gear", label: "Einstellungen" },
  { view: "help", icon: "lifering", label: "Hilfe-Anfragen" }
];

// Der Menüpunkt „Schule“ gehört nur der Schulleitung.
const schoolItem = { view: "school" as TeacherView, icon: "lightbulb", label: "Schule" };

export function TeacherShell(props: {
  user: AuthUser;
  classes: TeacherClass[];
  selectedClass?: TeacherClass;
  proposalCount?: number;
  concernCount?: number;
  view: TeacherView;
  sidebarOpen: boolean;
  onSidebarOpenChange: (open: boolean) => void;
  onClassSelect: (value: TeacherClass) => void;
  onCreateClass: () => void;
  onNavigate: (view: TeacherView) => void;
  onLogout: () => void;
  children: JSX.Element;
}) {
  const navigate = (view: TeacherView) => {
    props.onNavigate(view);
    props.onSidebarOpenChange(false);
  };

  return (
    <>
      <div class={`sidebar-backdrop ${props.sidebarOpen ? "visible" : ""}`} onClick={() => props.onSidebarOpenChange(false)} />
      <button class="menu-toggle" type="button" onClick={() => props.onSidebarOpenChange(!props.sidebarOpen)} style={{ opacity: props.sidebarOpen ? 0 : undefined }}>☰</button>
      <div class="app-container">
        <aside class={`sidebar ${props.sidebarOpen ? "open" : ""}`} id="sidebar">
          <div class="logo">
            <div class="logo-icon"><img src="/img/monster-badge.png" alt="Reflexo Monster" /></div>
            <div>
              <div class="logo-text">Refle<span class="logo-text-pop">xo</span></div>
              <div class="logo-version">Lehrer</div>
            </div>
          </div>

          <div class="class-selector-wrap">
            <select
              class="class-dropdown"
              aria-label="Klasse wählen"
              value={props.selectedClass?.id ?? ""}
              onChange={(event) => {
                const selected = props.classes.find((value) => value.id === event.currentTarget.value);
                if (selected) props.onClassSelect(selected);
              }}
            >
              {props.classes.length === 0
                ? <option value="">Noch keine Klasse</option>
                : <For each={props.classes}>{(cls) => (
                  <option value={cls.id}>{cls.name}</option>
                )}</For>}
            </select>
            <button class="class-add-btn" type="button" title="Neue Klasse erstellen" onClick={props.onCreateClass}>＋</button>
          </div>

          <div class="nav-section" style={{ "margin-top": "8px" }}>
            <For each={props.user.schoolRole === "owner" ? [navigation[0], schoolItem, ...navigation.slice(1)] : navigation}>{(item) => (
              <button class={`nav-item ${props.view === item.view ? "active" : ""}`} type="button" onClick={() => navigate(item.view)}>
                <StreetIcon name={item.icon} /> {item.label}
                {item.view === "goals" && (props.proposalCount ?? 0) > 0
                  ? <span style={{ background: "var(--warning-dark)", color: "#fff", "border-radius": "10px", "font-size": "0.7rem", "font-weight": 700, padding: "1px 7px", "margin-left": "auto" }}>{props.proposalCount}</span>
                  : null}
                {item.view === "feedback" && (props.concernCount ?? 0) > 0
                  ? <span style={{ background: "var(--danger-dark)", color: "#fff", "border-radius": "10px", "font-size": "0.7rem", "font-weight": 700, padding: "1px 7px", "margin-left": "auto" }}>{props.concernCount}</span>
                  : null}
              </button>
            )}</For>
          </div>

          <div class="nav-spacer" />
          <div class="user-card">
            <div class="user-name">{props.user.firstName} {props.user.lastName}</div>
            <div class="user-role"><StreetIcon name="person-single" size={14} /> {props.user.schoolRole === "owner" ? "Schulleitung" : "Lehrkraft"}</div>
            {props.user.schoolName && <div style={{ "font-size": "0.75rem", color: "var(--text-muted)", "margin-top": "2px" }}>{props.user.schoolName}</div>}
            <button class="nav-item" type="button" style={{ "margin-top": "8px", padding: "6px", "font-size": "0.8rem" }} onClick={props.onLogout}>
              <StreetIcon name="logout" size={18} /> Abmelden
            </button>
          </div>
        </aside>
        <main class="main-content">
          <div class="fade-in">{props.children}</div>
        </main>
      </div>
    </>
  );
}
